import { IQuoteRespSchema } from '@monetize/types/app';
import { useState } from 'react';
import {
  useGetAttachedQuoteDocuments,
  useGetQuoteStorageById,
} from '../../../../../../api/cpqService';
import { useConfirmModal } from '../../../../../../services/confirmModal';
import { SendQuoteManuallyContent } from './SendQuoteManuallyContent';
import { SendQuoteThroughEsignContent } from './SendQuoteThroughEsignContent';

/**
 * Show "Send" modal based on the quote status
 * If the quote requires eSign and eSign is enabled, show the eSign modal
 * If the quote does not require eSign or eSign is not enabled, show the manual send modal
 */
export const useQuoteSendModalActions = ({
  isEsignEnabled,
  quote,
  onSendQuote,
}: {
  isEsignEnabled: boolean;
  quote: IQuoteRespSchema;
  onSendQuote: () => void;
}) => {
  const [isSendViaDocuSign, setIsSendViaDocuSign] = useState(false);

  const attachedDocuments = useGetAttachedQuoteDocuments(quote.id, {
    enabled: isSendViaDocuSign,
  });

  const sowDocument = useGetQuoteStorageById(
    {
      quoteId: quote.id,
      storageId: quote.sowDocumentStorageId!,
      params: { attachContent: true },
    },
    { enabled: !!quote.sowDocumentStorageId },
  );

  const msaDocument = useGetQuoteStorageById(
    {
      quoteId: quote.id,
      storageId: quote.documentLocation!,
      params: { attachContent: true },
    },
    { enabled: !!quote.documentLocation },
  );

  const isDocumentsLoading =
    msaDocument.isLoading ||
    sowDocument.isLoading ||
    attachedDocuments.isLoading;

  const { showConfirmModal, closeModal } = useConfirmModal();

  const onClickSendViaDocusign = () => {
    setIsSendViaDocuSign(true);
    const internalContact = quote.contacts?.esign.find(
      (contact) => contact.internal,
    );
    const externalContact = quote.contacts?.esign.find(
      (contact) => !contact.internal,
    );
    showConfirmModal({
      title: `Would you like to send this quote through eSign?`,
      content: (
        <SendQuoteThroughEsignContent
          quote={quote}
          internalContact={internalContact}
          externalContact={externalContact}
          msaDocument={msaDocument}
          sowDocument={sowDocument}
          attachedDocuments={attachedDocuments}
        />
      ),
      onYes: () => {
        onSendQuote();
        closeModal();
      },
      yesButton: 'Send',
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
      modalBodyProps: {
        pb: 1,
      },
    });
  };

  const onClickSendManually = () => {
    showConfirmModal({
      title: 'Mark this Quote as Sent?',
      content: <SendQuoteManuallyContent quote={quote} />,
      onYes: () => {
        onSendQuote();
        closeModal();
      },
      yesButton: 'Confirm',
      yesBtnProps: {
        variant: 'primary',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  };

  const onClickSend = () => {
    if (quote.requiresEsign && isEsignEnabled) {
      onClickSendViaDocusign();
    } else {
      onClickSendManually();
    }
  };

  return {
    sowDocument,
    msaDocument,
    isDocumentsLoading,
    onClickSend,
  };
};
